/**
 * Admin AI Credits Page
 * =====================
 * 
 * Admin view for managing AI credits, promo codes, and usage stats.
 * Phase 3: Full CRUD for promo codes + generation history
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  ArrowLeft,
  Coins,
  Gift,
  BarChart3,
  Users,
  Image as ImageIcon,
  Video,
  Plus,
  Trash2,
  Loader2,
  RefreshCw,
  Copy,
  Sparkles,
  Pencil,
  History,
  ChevronRight,
  X,
  Clock,
  Eye,
} from "lucide-react";

const AdminAICreditsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  // Data
  const [stats, setStats] = useState(null);
  const [credits, setCredits] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [showEditPromoModal, setShowEditPromoModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  
  // Form states
  const [selectedMerchant, setSelectedMerchant] = useState("");
  const [assignCredits, setAssignCredits] = useState(100);
  const [assignReason, setAssignReason] = useState("Admin bonus");
  const [assigning, setAssigning] = useState(false);
  
  const [newPromoCode, setNewPromoCode] = useState("");
  const [newPromoCredits, setNewPromoCredits] = useState(100);
  const [newPromoMaxUses, setNewPromoMaxUses] = useState("");
  const [creatingPromo, setCreatingPromo] = useState(false);
  
  // Edit promo state
  const [editingPromo, setEditingPromo] = useState(null);
  const [editPromoCredits, setEditPromoCredits] = useState(100);
  const [editPromoMaxUses, setEditPromoMaxUses] = useState("");
  const [editPromoActive, setEditPromoActive] = useState(true);
  const [updatingPromo, setUpdatingPromo] = useState(false);
  
  // History state
  const [historyMerchant, setHistoryMerchant] = useState(null);
  const [merchantGenerations, setMerchantGenerations] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, creditsRes, promoRes, merchantsRes] = await Promise.all([
        fetch(`${API}/admin/ai/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/ai/credits/all`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/ai/promo-codes`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/users?role=merchant`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (statsRes.ok) setStats(await statsRes.json());
      if (creditsRes.ok) {
        const data = await creditsRes.json();
        setCredits(data.credits);
      }
      if (promoRes.ok) {
        const data = await promoRes.json();
        setPromoCodes(data.promo_codes);
      }
      if (merchantsRes.ok) {
        const data = await merchantsRes.json();
        setMerchants(data);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignCredits = async () => {
    if (!selectedMerchant || assignCredits <= 0) return;
    
    setAssigning(true);
    try {
      const res = await fetch(`${API}/admin/ai/credits/assign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          merchant_id: selectedMerchant,
          credits: assignCredits,
          reason: assignReason
        })
      });
      
      if (res.ok) {
        toast.success(`${assignCredits} credits assigned!`);
        setShowAssignModal(false);
        setSelectedMerchant("");
        setAssignCredits(100);
        setAssignReason("Admin bonus");
        fetchData();
      } else {
        toast.error("Failed to assign credits");
      }
    } catch (err) {
      toast.error("Error assigning credits");
    } finally {
      setAssigning(false);
    }
  };

  const handleCreatePromoCode = async () => {
    if (!newPromoCode.trim() || newPromoCredits <= 0) return;
    
    setCreatingPromo(true);
    try {
      const res = await fetch(`${API}/admin/ai/promo-codes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          code: newPromoCode,
          credits: newPromoCredits,
          max_uses: newPromoMaxUses ? parseInt(newPromoMaxUses) : null
        })
      });
      
      if (res.ok) {
        toast.success(`Promo code ${newPromoCode.toUpperCase()} created!`);
        setShowPromoModal(false);
        setNewPromoCode("");
        setNewPromoCredits(100);
        setNewPromoMaxUses("");
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to create promo code");
      }
    } catch (err) {
      toast.error("Error creating promo code");
    } finally {
      setCreatingPromo(false);
    }
  };

  const openEditPromoModal = (promo) => {
    setEditingPromo(promo);
    setEditPromoCredits(promo.credits);
    setEditPromoMaxUses(promo.max_uses?.toString() || "");
    setEditPromoActive(promo.active);
    setShowEditPromoModal(true);
  };

  const handleUpdatePromoCode = async () => {
    if (!editingPromo) return;
    
    setUpdatingPromo(true);
    try {
      const res = await fetch(`${API}/admin/ai/promo-codes/${editingPromo.code}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          credits: editPromoCredits,
          max_uses: editPromoMaxUses ? parseInt(editPromoMaxUses) : null,
          active: editPromoActive
        })
      });
      
      if (res.ok) {
        toast.success(`Promo code ${editingPromo.code} updated!`);
        setShowEditPromoModal(false);
        setEditingPromo(null);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to update promo code");
      }
    } catch (err) {
      toast.error("Error updating promo code");
    } finally {
      setUpdatingPromo(false);
    }
  };

  const handleDeletePromoCode = async (code) => {
    try {
      const res = await fetch(`${API}/admin/ai/promo-codes/${code}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Promo code deactivated");
        fetchData();
      }
    } catch (err) {
      toast.error("Error deactivating promo code");
    }
  };

  const openHistoryModal = async (merchantId, merchantName) => {
    setHistoryMerchant({ id: merchantId, name: merchantName });
    setShowHistoryModal(true);
    setLoadingHistory(true);
    
    try {
      const res = await fetch(`${API}/admin/ai/generations/all?merchant_id=${merchantId}&limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMerchantGenerations(data.generations);
      }
    } catch (err) {
      toast.error("Failed to load history");
    } finally {
      setLoadingHistory(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const getMerchantName = (merchantId) => {
    const merchant = merchants.find(m => m.user_id === merchantId);
    return merchant ? (merchant.business_name || `${merchant.first_name} ${merchant.last_name}`) : merchantId;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950">
      {/* Header */}
      <div className="bg-slate-900/80 backdrop-blur-sm border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate("/admin")}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft className="h-5 w-5 text-slate-400" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-400" />
                AI Credits Management
              </h1>
              <p className="text-slate-400 text-sm">Manage merchant credits and promo codes</p>
            </div>
          </div>
          
          <Button 
            onClick={fetchData}
            variant="outline" 
            className="bg-white/5 border-white/10 text-white hover:bg-white/10"
            data-testid="refresh-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl p-5 shadow-lg" data-testid="stat-total-generations">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <BarChart3 className="h-5 w-5 text-purple-600" />
              </div>
              <span className="text-slate-500 text-sm font-medium">Total Generations</span>
            </div>
            <p className="text-3xl font-bold text-slate-800">{stats?.total_generations || 0}</p>
          </div>
          
          <div className="bg-white rounded-2xl p-5 shadow-lg" data-testid="stat-images">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-pink-100">
                <ImageIcon className="h-5 w-5 text-pink-600" />
              </div>
              <span className="text-slate-500 text-sm font-medium">Images Generated</span>
            </div>
            <p className="text-3xl font-bold text-pink-600">{stats?.total_images || 0}</p>
          </div>
          
          <div className="bg-white rounded-2xl p-5 shadow-lg" data-testid="stat-videos">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-indigo-100">
                <Video className="h-5 w-5 text-indigo-600" />
              </div>
              <span className="text-slate-500 text-sm font-medium">Videos Generated</span>
            </div>
            <p className="text-3xl font-bold text-indigo-600">{stats?.total_videos || 0}</p>
          </div>
          
          <div className="bg-white rounded-2xl p-5 shadow-lg" data-testid="stat-credits">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <Coins className="h-5 w-5 text-amber-600" />
              </div>
              <span className="text-slate-500 text-sm font-medium">Credits in Circulation</span>
            </div>
            <p className="text-3xl font-bold text-amber-600">{stats?.total_credits_in_circulation || 0}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Merchant Credits */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-500" />
                Merchant Credits & History
              </h2>
              <Button size="sm" onClick={() => setShowAssignModal(true)} data-testid="assign-credits-btn">
                <Plus className="h-4 w-4 mr-1" />
                Assign Credits
              </Button>
            </div>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : credits.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                No credit records yet
              </div>
            ) : (
              <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
                {credits.map((credit) => (
                  <div 
                    key={credit.merchant_id} 
                    className="p-4 hover:bg-slate-50 flex items-center justify-between group"
                    data-testid={`merchant-credit-${credit.merchant_id}`}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{getMerchantName(credit.merchant_id)}</p>
                      <p className="text-xs text-slate-500 font-mono">{credit.merchant_id}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-lg font-bold text-amber-600">{credit.balance}</p>
                        <p className="text-xs text-slate-500">credits</p>
                      </div>
                      <button
                        onClick={() => openHistoryModal(credit.merchant_id, getMerchantName(credit.merchant_id))}
                        className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors opacity-0 group-hover:opacity-100"
                        title="View generation history"
                        data-testid={`view-history-${credit.merchant_id}`}
                      >
                        <History className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Promo Codes */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                <Gift className="h-5 w-5 text-green-500" />
                Promo Codes
              </h2>
              <Button 
                size="sm" 
                onClick={() => setShowPromoModal(true)} 
                className="bg-green-600 hover:bg-green-700"
                data-testid="create-promo-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Create Code
              </Button>
            </div>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : promoCodes.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                No promo codes yet
              </div>
            ) : (
              <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
                {promoCodes.map((promo) => (
                  <div 
                    key={promo.code} 
                    className="p-4 hover:bg-slate-50 group"
                    data-testid={`promo-code-${promo.code}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <code className="px-2 py-1 bg-slate-100 rounded text-sm font-bold text-slate-800">
                          {promo.code}
                        </code>
                        <button
                          onClick={() => copyToClipboard(promo.code)}
                          className="text-slate-400 hover:text-slate-600"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        {promo.active ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Active</span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Inactive</span>
                        )}
                        <button
                          onClick={() => openEditPromoModal(promo)}
                          className="text-slate-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Edit promo code"
                          data-testid={`edit-promo-${promo.code}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        {promo.active && (
                          <button
                            onClick={() => handleDeletePromoCode(promo.code)}
                            className="text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`delete-promo-${promo.code}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Coins className="h-4 w-4 text-amber-500" />
                        {promo.credits} credits
                      </span>
                      <span>
                        {promo.redeemed_by?.length || 0} / {promo.max_uses || "∞"} used
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Assign Credits Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Credits to Merchant</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Select Merchant</Label>
              <select
                value={selectedMerchant}
                onChange={(e) => setSelectedMerchant(e.target.value)}
                className="w-full mt-1 p-2 border rounded-lg bg-slate-50"
                data-testid="select-merchant"
              >
                <option value="">Select a merchant...</option>
                {merchants.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.business_name || `${m.first_name} ${m.last_name}`} ({m.email})
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <Label>Credits Amount</Label>
              <Input
                type="number"
                value={assignCredits}
                onChange={(e) => setAssignCredits(parseInt(e.target.value) || 0)}
                min={1}
                className="mt-1"
                data-testid="assign-credits-amount"
              />
            </div>
            
            <div>
              <Label>Reason</Label>
              <Input
                value={assignReason}
                onChange={(e) => setAssignReason(e.target.value)}
                placeholder="e.g., Welcome bonus"
                className="mt-1"
                data-testid="assign-credits-reason"
              />
            </div>
            
            <Button 
              onClick={handleAssignCredits}
              disabled={!selectedMerchant || assignCredits <= 0 || assigning}
              className="w-full"
              data-testid="confirm-assign-btn"
            >
              {assigning ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Coins className="h-4 w-4 mr-2" />
              )}
              Assign {assignCredits} Credits
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Promo Code Modal */}
      <Dialog open={showPromoModal} onOpenChange={setShowPromoModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Promo Code</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Promo Code</Label>
              <Input
                value={newPromoCode}
                onChange={(e) => setNewPromoCode(e.target.value.toUpperCase())}
                placeholder="e.g., WELCOME50"
                className="mt-1 uppercase"
                data-testid="new-promo-code"
              />
            </div>
            
            <div>
              <Label>Credits Value</Label>
              <Input
                type="number"
                value={newPromoCredits}
                onChange={(e) => setNewPromoCredits(parseInt(e.target.value) || 0)}
                min={1}
                className="mt-1"
                data-testid="new-promo-credits"
              />
            </div>
            
            <div>
              <Label>Max Uses (optional)</Label>
              <Input
                type="number"
                value={newPromoMaxUses}
                onChange={(e) => setNewPromoMaxUses(e.target.value)}
                placeholder="Unlimited"
                min={1}
                className="mt-1"
                data-testid="new-promo-max-uses"
              />
              <p className="text-xs text-slate-500 mt-1">Leave empty for unlimited uses</p>
            </div>
            
            <Button 
              onClick={handleCreatePromoCode}
              disabled={!newPromoCode.trim() || newPromoCredits <= 0 || creatingPromo}
              className="w-full bg-green-600 hover:bg-green-700"
              data-testid="confirm-create-promo-btn"
            >
              {creatingPromo ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Gift className="h-4 w-4 mr-2" />
              )}
              Create Promo Code
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Promo Code Modal */}
      <Dialog open={showEditPromoModal} onOpenChange={setShowEditPromoModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-blue-500" />
              Edit Promo Code: {editingPromo?.code}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Credits Value</Label>
              <Input
                type="number"
                value={editPromoCredits}
                onChange={(e) => setEditPromoCredits(parseInt(e.target.value) || 0)}
                min={1}
                className="mt-1"
                data-testid="edit-promo-credits"
              />
            </div>
            
            <div>
              <Label>Max Uses</Label>
              <Input
                type="number"
                value={editPromoMaxUses}
                onChange={(e) => setEditPromoMaxUses(e.target.value)}
                placeholder="Unlimited"
                min={1}
                className="mt-1"
                data-testid="edit-promo-max-uses"
              />
              <p className="text-xs text-slate-500 mt-1">Leave empty for unlimited uses</p>
            </div>
            
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
              <div>
                <Label className="text-slate-700">Active Status</Label>
                <p className="text-xs text-slate-500">Toggle to enable/disable this code</p>
              </div>
              <Switch
                checked={editPromoActive}
                onCheckedChange={setEditPromoActive}
                data-testid="edit-promo-active"
              />
            </div>
            
            {editingPromo && (
              <div className="p-3 rounded-lg bg-slate-50 text-sm text-slate-600">
                <p><span className="font-medium">Used:</span> {editingPromo.redeemed_by?.length || 0} times</p>
                <p><span className="font-medium">Created:</span> {new Date(editingPromo.created_at).toLocaleDateString()}</p>
              </div>
            )}
            
            <Button 
              onClick={handleUpdatePromoCode}
              disabled={editPromoCredits <= 0 || updatingPromo}
              className="w-full"
              data-testid="confirm-edit-promo-btn"
            >
              {updatingPromo ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Pencil className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Generation History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-purple-500" />
              Generation History: {historyMerchant?.name}
            </DialogTitle>
          </DialogHeader>
          
          {loadingHistory ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : merchantGenerations.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No generations yet for this merchant
            </div>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
              {merchantGenerations.map((gen) => (
                <div 
                  key={gen.generation_id} 
                  className="p-4 rounded-xl bg-slate-50 border border-slate-100"
                  data-testid={`generation-${gen.generation_id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg shrink-0 ${gen.type === "image" ? "bg-purple-100" : "bg-pink-100"}`}>
                      {gen.type === "image" ? (
                        <ImageIcon className="h-5 w-5 text-purple-600" />
                      ) : (
                        <Video className="h-5 w-5 text-pink-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          gen.type === "image" 
                            ? "bg-purple-100 text-purple-700" 
                            : "bg-pink-100 text-pink-700"
                        }`}>
                          {gen.type === "image" ? "Image" : "Video"}
                        </span>
                        <span className="text-xs text-slate-400">
                          {gen.credits_used} credits
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 mb-2">{gen.prompt}</p>
                      <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(gen.created_at).toLocaleString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </span>
                        <span className="px-2 py-0.5 rounded bg-slate-200">
                          {gen.style}
                        </span>
                        <span className="px-2 py-0.5 rounded bg-slate-200">
                          {gen.aspect_ratio}
                        </span>
                        {gen.duration && (
                          <span className="px-2 py-0.5 rounded bg-slate-200">
                            {gen.duration}s
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAICreditsPage;
